import { useMemo, useState, type ReactNode } from 'react';
import {
  A2UI_PROTOCOL_VERSION,
  VALO_A2UI_CATALOG_ID,
  dispatchA2UIAction,
  parseValoA2UIStream,
  resolveA2UIValue,
  type A2UIActionDispatch,
  type ValoA2UIComponent,
  type ValoA2UIMessage,
} from './a2ui-cockpit';

const demoMessages: ValoA2UIMessage[] = [
  {
    version: A2UI_PROTOCOL_VERSION,
    createSurface: {
      surfaceId: 'unified-cockpit-a2ui-demo',
      catalogId: VALO_A2UI_CATALOG_ID,
      sendDataModel: true,
    },
  },
  {
    version: A2UI_PROTOCOL_VERSION,
    updateDataModel: {
      surfaceId: 'unified-cockpit-a2ui-demo',
      value: {
        proposedAction: {
          id: 'a2ui-demo-action',
          principalId: 'human-owner',
          profile: 'professional',
          capability: 'SPEND_MONEY',
          purpose: 'approved_purchase',
          amountMinor: 125000,
          currency: 'NOK',
          recipient: 'supplier-a',
          channel: 'personal-agency-os',
          risk: 'medium',
          reversible: false,
          external: true,
          dataScopes: [],
          evidence: ['evidence:quote:1'],
        },
      },
    },
  },
  {
    version: A2UI_PROTOCOL_VERSION,
    updateComponents: {
      surfaceId: 'unified-cockpit-a2ui-demo',
      components: [
        { id: 'root', component: 'Column', children: ['intro', 'standing', 'intent', 'submit'] },
        {
          id: 'intro',
          component: 'Text',
          text: 'Agent-generated UI proposes. VALO authorizes.',
        },
        {
          id: 'standing',
          component: 'StandingCard',
          title: 'Present standing',
          body: 'Evidence and authority are visible before action.',
          status: 'CURRENT',
        },
        {
          id: 'intent',
          component: 'ActionIntent',
          title: 'Purchase order proposal',
          body: 'NOK 1,250.00 → supplier-a',
          status: 'NOT AUTHORIZED',
        },
        { id: 'submit-label', component: 'Text', text: 'Request governed execution' },
        {
          id: 'submit',
          component: 'Button',
          child: 'submit-label',
          action: {
            event: {
              name: 'REQUEST_GOVERNED_EXECUTION',
              context: { proposedAction: { path: '/proposedAction' } },
            },
          },
        },
      ],
    },
  },
];

function display(value: unknown) {
  if (value === undefined || value === null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return JSON.stringify(value);
}

export function A2UICockpitSurface() {
  const state = useMemo(() => parseValoA2UIStream(demoMessages), []);
  const [dispatch, setDispatch] = useState<A2UIActionDispatch>();

  const renderComponent = (id: string, ancestors = new Set<string>()): ReactNode => {
    const component = state.components.get(id);
    if (!component || ancestors.has(id)) return null;

    const nextAncestors = new Set(ancestors);
    nextAncestors.add(id);
    const value = (input: unknown) => display(resolveA2UIValue(input, state.dataModel));
    const children = (component.children ?? []).map((childId) => (
      <div key={childId}>{renderComponent(childId, nextAncestors)}</div>
    ));
    const child = component.child ? renderComponent(component.child, nextAncestors) : null;

    if (component.component === 'Text') return <p>{value(component.text)}</p>;
    if (component.component === 'Divider') return <hr />;
    if (component.component === 'Row') return <div className="cockpit-flow">{children}</div>;
    if (component.component === 'Column') return <div className="cockpit-panel">{children}</div>;
    if (component.component === 'Card') return <div className="cockpit-panel">{child ?? children}</div>;
    if (component.component === 'Button') {
      return <button type="button" onClick={() => setDispatch(dispatchA2UIAction(state, component.id))}>{child ?? 'Action'}</button>;
    }

    return <GovernanceComponent component={component} value={value}>{children}</GovernanceComponent>;
  };

  return <div className="cockpit-grid">
    <div>
      <p className="eyebrow">A2UI v0.9.1 compatible</p>
      {renderComponent('root')}
    </div>
    <div className="cockpit-panel cockpit-output">
      <div><span>Catalog</span><strong>VALO cockpit v1</strong><small>No arbitrary components or local code</small></div>
      <div><span>UI authority</span><strong>NONE</strong><small>A2UI can propose, never authorize</small></div>
      <div><span>Execution gate</span><strong>reht</strong><small>RACS binding → gateway → Veritas</small></div>
      <div><span>Last interaction</span><strong>{dispatch ? dispatch.eventName : 'None'}</strong><small>{dispatch ? `${dispatch.proposedAction.id} · ${dispatch.executionAuthority}` : 'No action intent materialized'}</small></div>
    </div>
  </div>;
}

function GovernanceComponent({ component, value, children }: {
  component: ValoA2UIComponent;
  value: (input: unknown) => string;
  children: ReactNode;
}) {
  return <article className="cockpit-panel">
    <p className="eyebrow">{component.component}</p>
    {component.title ? <h3>{value(component.title)}</h3> : null}
    {component.body ? <p>{value(component.body)}</p> : null}
    {component.status ? <small>{value(component.status)}</small> : null}
    {children}
  </article>;
}
